import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { FiClock, FiPlus } from 'react-icons/fi';
import { RootState } from '@/store';
import { DeviceSelector } from '@/components/shared/DeviceSelectorNew';
import useGlobalDeviceSelector from '@/hooks/useGlobalDeviceSelector';
import { apiService } from '@/utils/api';
import { Card } from '@/components/ui/Card';
import { SchedulePanelOverview } from '@/components/ui/DeviceCard/schedulePanelOverview';
import { useNavigate } from 'react-router-dom';
import { getWeekdayArray } from '@/utils/utils';
import { formatMinutesToTime } from '@/utils/time';
import type { ScheduleInfoResponse,ScheduleOverviewResponse, AuxWithSchedules, ScheduleInfo, Aux, Schedule, Manual, TimeInfo, Pump } from '@/types/dashboard';
import { AuxTypeCode, getAuxTypeDesc } from '@/types/device';
import { setLoading } from '@/store/slices/appSlice';
import Sidebar from '@/components/ui/Sidebar/Sidebar';
import TimeInput from '@/components/ui/TimeInput';
import Modal from '@/components/ui/Modal/Modal';
import ScheduleDeviceSummary from '@/components/ui/Schedule/ScheduleDeviceSummary';
import ScheduleTimeRepeatForm from '@/components/ui/Schedule/ScheduleTimeRepeatForm';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { GetPumpInfoResponse } from '@/types/api';
import { isThreeSpeedPump, snapThreeSpeedSpeed, THREE_SPEED_LOW } from '@/utils/utils';

const SectionHeader: React.FC<{ title: string; subtitle?: string; right?: React.ReactNode }>=({ title, subtitle, right }) => {
  const { mode } = useSelector((state: RootState) => state.theme);
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="flex items-center gap-2">
          <FiClock className="text-primary-500 text-xl" />
          <h2 className={classNames(
            'text-lg font-semibold',
            { 'text-gray-900': mode === 'light', 'text-gray-200': mode === 'dark' }
          )}>{title}</h2>
        </div>
        {subtitle && (
          <p className={classNames(
            'mt-1 text-sm',
            { 'text-gray-500': mode === 'light', 'text-gray-400': mode === 'dark' }
          )}>{subtitle}</p>
        )}
      </div>
      {right}
    </div>
  );
};

const ScheduleOverviewPage: React.FC = () => {
  const { selectedDeviceId, onDeviceChange } = useGlobalDeviceSelector();
  const { mode } = useSelector((state: RootState) => state.theme);
  const navigate = useNavigate();

  const [timeText, setTimeText] = useState('');
  const [scheduleInfo, setScheduleInfo] = useState<ScheduleInfo | null>(null);
  const [pumpAux, setPumpAux] = useState<Aux | null>(null);
  const [pumpInfo, setPumpInfo] = useState<Pump | null>(null);
  const [manual, setManual] = useState<Manual| null>(null);
  const [spaList, setSpaList] = useState<AuxWithSchedules[]>([]);
  const [spaDevices, setSpaDevices] = useState<AuxWithSchedules[]>([]);
  const [otherDevices, setOtherDevices] = useState<AuxWithSchedules[]>([]);
  const [pumpLinkedDevices, setPumpLinkedDevices] = useState<AuxWithSchedules[]>([]);
  const [schedulePumpSpeed, setSchedulePumpSpeed] = useState<number>(0);
  // 编辑 SPA 组（RETURN/SUCTION/EXTRA1/EXTRA2）的集合，用于一次性编辑
  const [spaEditingGroup, setSpaEditingGroup] = useState<Schedule[] | null>(null);

  // 编辑侧边栏相关状态
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [editName, setEditName] = useState('');
  const [editStart, setEditStart] = useState<Date | null>(null);
  const [editEnd, setEditEnd] = useState<Date | null>(null);
  const [editDays, setEditDays] = useState<boolean[]>([false, false, false, false, false, false, false]);
  // Add Schedule modal states
  const [isAddOpen, setIsAddOpen] = useState(false);
  // Delete confirmation dialog state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'main_pump' | 'spa_group' | 'single'; scheduleId: number } | null>(null);

  const promptDelete = (kind: 'main_pump' | 'spa_group' | 'single', scheduleId: number) => {
    setDeleteTarget({ kind, scheduleId });
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      setConfirmDeleteOpen(false);
      return;
    }
    const { kind, scheduleId } = deleteTarget;
    if (kind === 'main_pump') {
      await handleDeleteMainPump(scheduleId);
    } else if (kind === 'spa_group') {
      await handleDeleteSpaGroup(scheduleId);
    } else {
      await handleDeleteSingleSchedule(scheduleId);
    }
    setConfirmDeleteOpen(false);
    setDeleteTarget(null);
  };
  const [addStep, setAddStep] = useState<'select' | 'configure'>('select');
  const [selectMainPump, setSelectMainPump] = useState(false);
  const [selectedLinkedSlots, setSelectedLinkedSlots] = useState<number[]>([]);
  // Add step: single-radio device selection
  const [selectedAddDevice, setSelectedAddDevice] = useState<{
    kind: 'pump' | 'spa' | 'spa_device' | 'other_device';
    aux?: Aux;
  } | null>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchOverview = async () => {
      if (!selectedDeviceId) return;
      dispatch(setLoading(true));
      try {
        const [response, pumpInfoResp] = await Promise.all([
          apiService.get<ScheduleOverviewResponse>(`/api/v1/iot-shadow/device/${selectedDeviceId}/scheduleOverview`),
          apiService.get<GetPumpInfoResponse>(`/api/v1/iot-shadow/device/${selectedDeviceId}/pumpInfo`),
        ]);
        setPumpInfo(pumpInfoResp.pump);

        // 时间信息
        const tzText = response.timeInfo?.time_zone_id ? ` (${response.timeInfo.time_zone_id})` : '';
        setTimeText(`${response.timeInfo?.time || ''}${tzText}`);

        // 主泵 aux
        const mainPump = response.manual.aux.find(aux => aux.aux_type_code === AuxTypeCode.SPALSHME_PUMP) || null;
        if (mainPump) {
          const threeSpeed = isThreeSpeedPump(pumpInfoResp.pump?.pump_brand, pumpInfoResp.pump?.pump_model);
          const nextPumpAux = { ...mainPump };
          if (threeSpeed) {
            nextPumpAux.pump_speed = snapThreeSpeedSpeed((nextPumpAux.pump_speed ?? 0) > 0 ? nextPumpAux.pump_speed : THREE_SPEED_LOW);
          }
          setPumpAux(nextPumpAux);
        } else {
          setPumpAux(null);
        }
        setManual(response.manual);     
        updateScheduleInfo(response);
        
      } catch (e) {
        console.error('Failed to fetch schedule overview:', e);
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchOverview();
  }, [selectedDeviceId]);

  const mainPumpSchedules = useMemo(() => {
    const list = scheduleInfo?.schedules || [];
    return list.filter(s => s.aux_type_code === AuxTypeCode.SPALSHME_PUMP).sort((a, b) => a.schedule_id - b.schedule_id);
  }, [scheduleInfo]);

  // 将联动设备中与主泵排程完全一致（开始/结束时间、天数与开关状态相同）的设备名称收集起来
  const schedulesEqual = (a?: Schedule, b?: Schedule): boolean => {
    if (!a || !b) return false;
    const daysA = a.week_days || [];
    const daysB = b.week_days || [];
    const sameDays = daysA.length === daysB.length && daysA.every((v, i) => v === daysB[i]);
    return (
      a.start_time === b.start_time &&
      a.end_time === b.end_time &&
      a.schedule_state === b.schedule_state &&
      sameDays
    );
  };

  // 聚合 SPA 模式：优先使用吸水阀（SUCTION）的排程，否则使用回水阀（RETURN）的排程
  const spaSuctionItem = useMemo(() =>
    spaList.find(d => d.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_SUCTION),
    [spaList]
  );
  const spaReturnItem = useMemo(() =>
    spaList.find(d => d.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_RETURN),
    [spaList]
  );
  // SPA 模式分组：对 RETURN/SUCTION/EXTRA1/EXTRA2 按索引配对，渲染每一组
  const spaModeGroups = useMemo(
    () => generateSpaScheduleSet(scheduleInfo?.schedules || []),
    [scheduleInfo]
  );

  // 基于 useMemo 预计算：当 pump 的 schedule 或 linkedDevice 变化时，自动更新映射
  const linkedDeviceNamesMap = useMemo(() => {
    const map = new Map<number, Schedule[]>();
    for (const pumpSchedule of mainPumpSchedules) {
      map.set(pumpSchedule.schedule_id, []);
    }
    const linkedSchedules = pumpLinkedDevices.flatMap(dev => dev.schedules);
    for (const linkedSchedule of linkedSchedules) {
      for (const pumpSchedule of mainPumpSchedules) {
        console.log('pumpSchedule', pumpSchedule,", linkedSchedule", linkedSchedule, "equal?", schedulesEqual(pumpSchedule, linkedSchedule));
        if (schedulesEqual(pumpSchedule, linkedSchedule)) {
          map.get(pumpSchedule.schedule_id)?.push(linkedSchedule);
        }
      } 
    }
    return map;
  }, [mainPumpSchedules, pumpLinkedDevices]);

  const getLinkedDeviceNames = (pumpSchedule: Schedule): string[] => {
    return linkedDeviceNamesMap.get(pumpSchedule.schedule_id)?.map(s => s.schedule_name || '') || [];
  };

  const getLinkedDeviceSchedules = (schedule_id: number): Schedule[] => {
    return linkedDeviceNamesMap.get(schedule_id) || [];
  };



  const updateScheduleInfo = (response: ScheduleOverviewResponse) => {
    setScheduleInfo(response.scheduleInfo);
    // 构建各设备的排程列表
    const schedules = response.scheduleInfo?.schedules || [];
    const makeAuxWithSchedules = (auxList: Aux[]): AuxWithSchedules[] =>
      auxList.map(aux => ({
        aux,
        schedules: schedules
          .filter((s) => s.aux_type_code === aux.aux_type_code && (s.aux_slot === aux.aux_slot_num || s.slot_num === aux.aux_slot_num))
          .sort((a, b) => a.schedule_id - b.schedule_id),
      })).filter(item => item.schedules.length > 0);
    setPumpLinkedDevices(makeAuxWithSchedules(response.manual.pump_linked_devices || []));
    setSpaList(makeAuxWithSchedules(response.manual.spa || []));
    setSpaDevices(makeAuxWithSchedules(response.manual.spa_devices || []));
    setOtherDevices(makeAuxWithSchedules(response.manual.other_devices || []));
  };

  // 生成 SPA 排程集合：按照 Android 的 generateSpaScheduleSet 逻辑，
  // 将 RETURN、SUCTION、EXTRA_1、EXTRA_2 各自的列表按索引对齐成一组
  function generateSpaScheduleSet(allSchedules: Schedule[]): (Array<Schedule | undefined>)[] {
    const filtered = allSchedules.filter(s =>
      [
        AuxTypeCode.SPA_ACTUATOR_RETURN,
        AuxTypeCode.SPA_ACTUATOR_SUCTION,
        AuxTypeCode.SPA_ACTUATOR_EXTRA_1,
        AuxTypeCode.SPA_ACTUATOR_EXTRA_2,
      ].includes(s.aux_type_code)
    );

    const spaReturn = filtered
      .filter(s => s.aux_type_code === AuxTypeCode.SPA_ACTUATOR_RETURN)
      .sort((a, b) => a.schedule_id - b.schedule_id);
    const spaSuction = filtered
      .filter(s => s.aux_type_code === AuxTypeCode.SPA_ACTUATOR_SUCTION)
      .sort((a, b) => a.schedule_id - b.schedule_id);
    const spaExtra1 = filtered
      .filter(s => s.aux_type_code === AuxTypeCode.SPA_ACTUATOR_EXTRA_1)
      .sort((a, b) => a.schedule_id - b.schedule_id);
    const spaExtra2 = filtered
      .filter(s => s.aux_type_code === AuxTypeCode.SPA_ACTUATOR_EXTRA_2)
      .sort((a, b) => a.schedule_id - b.schedule_id);

    const maxSize = Math.max(spaReturn.length, spaSuction.length, spaExtra1.length, spaExtra2.length);
    const set: (Array<Schedule | undefined>)[] = [];
    for (let i = 0; i < maxSize; i++) {
      set.push([
        spaReturn[i],
        spaSuction[i],
        spaExtra1[i],
        spaExtra2[i],
      ]);
    }
    return set;
  }

  const handleSpaToggleEnabled = async (scheduleId: number, enabled: boolean) => {
    if (!selectedDeviceId) return;

    // 基于当前所有排程，按索引配对 SUCTION/RETURN 等排程
    const all = scheduleInfo?.schedules || [];
    const spaSet = generateSpaScheduleSet(all);

    // 找到目标 schedule 所在的配对组及其成员
    let targetGroup: Array<Schedule | undefined> | undefined;
    for (const group of spaSet) {
      if (group.some(s => s && s.schedule_id === scheduleId)) {
        targetGroup = group;
        break;
      }
    }

    if (!targetGroup) {
      // 未找到配对组，退化为单个排程的开关逻辑
      await handleToggleEnabled(scheduleId, enabled);
      return;
    }

    // 乐观更新：同步更新本地 scheduleInfo 中对应成员的状态
    setScheduleInfo(prev => {
      if (!prev) return prev;
      const idsToUpdate = targetGroup
        .filter(Boolean)
        .map(s => (s as Schedule).schedule_id);
      return {
        ...prev,
        schedules: prev.schedules.map(s =>
          idsToUpdate.includes(s.schedule_id) ? { ...s, schedule_state: enabled } : s
        ),
      };
    });

    dispatch(setLoading(true));
    try {
      // 依次发送两个（或更多）请求，模仿 Android 的顺序与延迟
      const members = targetGroup.filter(Boolean) as Schedule[];
      if (members.length > 0) {
        await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/changeScheduleStatus`, {
          schedule_id: members[0].schedule_id,
          schedule_state: enabled,
        });
      }
      if (members.length > 1) {
        await new Promise(res => setTimeout(res, 1000));
        await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/changeScheduleStatus`, {
          schedule_id: members[1].schedule_id,
          schedule_state: enabled,
        });
      }
    } catch (e) {
      console.error('Failed to change SPA schedules status:', e);
    } finally {
      await fetchScheduleInfo();
      dispatch(setLoading(false));
    }
  }

  const handleToggleEnabled = async (scheduleId: number, enabled: boolean) => {
    setScheduleInfo(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        schedules: prev.schedules.map(s => s.schedule_id === scheduleId ? { ...s, schedule_state: enabled } : s),
      };
    });
    const linkedSchedules = getLinkedDeviceSchedules(scheduleId);
    dispatch(setLoading(true));
    try {
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/changeScheduleStatus`, {
        schedule_id: scheduleId,
        schedule_state: enabled,
      });
      // 顺序执行，确保 await 生效；如需并行可改为 Promise.all
      for (const s of linkedSchedules) {
        await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/changeScheduleStatus`, {
          schedule_id: s.schedule_id,
          schedule_state: enabled,
        });
      }
    } catch (e) {
      console.error('Failed to change schedule status:', e);
      // 失败时回滚状态
      setScheduleInfo(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          schedules: prev.schedules.map(s => s.schedule_id === scheduleId ? { ...s, schedule_state: !enabled } : s),
        };
      });
    } finally {
      await new Promise(res => setTimeout(res, 3000));
      await fetchScheduleInfo();
      dispatch(setLoading(false));
    }
  };

  const handleSpaEditSchedule = (scheduleId: number) => {
    const all = scheduleInfo?.schedules || [];
    const spaSet = generateSpaScheduleSet(all);

    // 找到该 schedule 所在的组
    let targetGroup: Array<Schedule | undefined> | undefined;
    for (const group of spaSet) {
      if (group.some(s => s && s.schedule_id === scheduleId)) {
        targetGroup = group;
        break;
      }
    }
    if (!targetGroup) return;

    // 选择主编辑对象：优先 SUCTION，其次 RETURN，再次 EXTRA1、EXTRA2
    const primary = targetGroup[1] || targetGroup[0] || targetGroup[2] || targetGroup[3];
    const members = targetGroup.filter(Boolean) as Schedule[];
    if (!primary) return;

    // 保存组成员用于批量更新
    setSpaEditingGroup(members);

    // 打开侧边栏编辑主对象
    const target = primary as Schedule;
    setEditingSchedule(target);
    setEditName(target.schedule_name || '');
    const minutesToDate = (min: number) => {
      const base = new Date();
      base.setHours(0, 0, 0, 0);
      const h = Math.floor(min / 60);
      const m = min % 60;
      base.setHours(h, m, 0, 0);
      return base;
    };
    setEditStart(minutesToDate(target.start_time));
    setEditEnd(minutesToDate(target.end_time));
    setEditDays(target.week_days?.length ? [...target.week_days] : [false, false, false, false, false, false, false]);
    setIsEditOpen(true);
  }

  const handleEditSchedule = (scheduleId: number) => {
    const target = scheduleInfo?.schedules.find(s => s.schedule_id === scheduleId) || null;
    if (!target) return;
    setEditingSchedule(target);
    setEditName(target.schedule_name || '');
    if (target.aux_type_code === AuxTypeCode.SPALSHME_PUMP) {
      const threeSpeed = isThreeSpeedPump(pumpInfo?.pump_brand, pumpInfo?.pump_model);
      const raw = target.pump_speed ?? 0;
      setSchedulePumpSpeed(
        threeSpeed
          ? snapThreeSpeedSpeed(raw > 0 ? raw : THREE_SPEED_LOW)
          : (raw > 0 ? raw : 50)
      );
    }
    // 将分钟转换为当天的 Date
    const minutesToDate = (min: number) => {
      const base = new Date();
      base.setHours(0, 0, 0, 0);
      const h = Math.floor(min / 60);
      const m = min % 60;
      base.setHours(h, m, 0, 0);
      return base;
    };
    setEditStart(minutesToDate(target.start_time));
    setEditEnd(minutesToDate(target.end_time));
    setEditDays(target.week_days?.length ? [...target.week_days] : [false, false, false, false, false, false, false]);
    setIsEditOpen(true);
  };

  // 删除：单个排程（通用）
  const handleDeleteSingleSchedule = async (scheduleId: number) => {
    if (!selectedDeviceId) return;
    // 乐观移除
    setScheduleInfo(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        schedules: prev.schedules.filter(s => s.schedule_id !== scheduleId),
      };
    });
    dispatch(setLoading(true));
    try {
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/deleteSchedule`, {
        schedule_num: scheduleId,
      });
    } catch (e) {
      console.error('Failed to delete schedule:', e);
      // 失败时刷新恢复
    } finally {
      await new Promise(res => setTimeout(res, 1500));
      await fetchScheduleInfo();
      dispatch(setLoading(false));
    }
  };

  // 删除：主泵排程及其联动设备（参照 Android deleteMainPumpSchedule）
  const handleDeleteMainPump = async (scheduleId: number) => {
    if (!selectedDeviceId) return;
    const linkedSchedules = getLinkedDeviceSchedules(scheduleId);
    // 乐观移除主泵与联动
    setScheduleInfo(prev => {
      if (!prev) return prev;
      const linkedIds = linkedSchedules.map(s => s.schedule_id);
      return {
        ...prev,
        schedules: prev.schedules.filter(s => s.schedule_id !== scheduleId && !linkedIds.includes(s.schedule_id)),
      };
    });
    dispatch(setLoading(true));
    try {
      // 先删主泵
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/deleteSchedule`, {
        schedule_num: scheduleId,
      });
      // 依次删除联动设备，间隔 1 秒
      for (let i = 0; i < linkedSchedules.length; i++) {
        const s = linkedSchedules[i];
        await new Promise(res => setTimeout(res, 1000));
        await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/deleteSchedule`, {
          schedule_num: s.schedule_id,
        });
      }
    } catch (e) {
      console.error('Failed to delete main pump and linked schedules:', e);
    } finally {
      await new Promise(res => setTimeout(res, 1500));
      await fetchScheduleInfo();
      dispatch(setLoading(false));
    }
  };

  // 删除：SPA 分组排程（参照 Android deleteSpaSchedule）
  const handleDeleteSpaGroup = async (scheduleId: number) => {
    if (!selectedDeviceId) return;
    const all = scheduleInfo?.schedules || [];
    const spaSet = generateSpaScheduleSet(all);
    let targetGroup: Array<Schedule | undefined> | undefined;
    for (const group of spaSet) {
      if (group.some(s => s && s.schedule_id === scheduleId)) {
        targetGroup = group;
        break;
      }
    }
    if (!targetGroup) {
      // 找不到组则退化为单个删除
      await handleDeleteSingleSchedule(scheduleId);
      return;
    }
    const members = targetGroup.filter(Boolean) as Schedule[];
    // 乐观移除组成员
    setScheduleInfo(prev => {
      if (!prev) return prev;
      const ids = members.map(s => s.schedule_id);
      return {
        ...prev,
        schedules: prev.schedules.filter(s => !ids.includes(s.schedule_id)),
      };
    });
    dispatch(setLoading(true));
    try {
      for (let i = 0; i < members.length; i++) {
        const s = members[i];
        await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/deleteSchedule`, {
          schedule_num: s.schedule_id,
        });
        if (i < members.length - 1) {
          await new Promise(res => setTimeout(res, 1000));
        }
      }
    } catch (e) {
      console.error('Failed to delete SPA group schedules:', e);
    } finally {
      await new Promise(res => setTimeout(res, 1500));
      await fetchScheduleInfo();
      dispatch(setLoading(false));
    }
  };

  const fetchScheduleInfo = async ()  => {
    try {
      const response = await apiService.get<ScheduleInfoResponse>(`/api/v1/iot-shadow/device/${selectedDeviceId}/scheduleInfo`);
      var scheduleOverviewResponse: ScheduleOverviewResponse = {
        scheduleInfo: response.scheduleInfo,
        manual: manual || ({} as Manual),
        timeInfo: {} as TimeInfo,
      };
      updateScheduleInfo(scheduleOverviewResponse);
    } catch (e) {
      console.error('Failed to fetch schedule info:', e);
      return e;
    }
    return null;
  };

  const handleSaveSchedule = async () => {
    if (!editingSchedule || !selectedDeviceId) return;

    const dateToMinutes = (d: Date) => d.getHours() * 60 + d.getMinutes();

    // 若为 SPA 组编辑：对组内每个成员按同一时间/天数/名称进行更新
    if (spaEditingGroup && spaEditingGroup.some(s => s.schedule_id === editingSchedule.schedule_id)) {
      dispatch(setLoading(true));
      try {
        for (let i = 0; i < spaEditingGroup.length; i++) {
          const s = spaEditingGroup[i];
          const payload: any = {
            aux_slot: s.aux_slot,
            schedule_num: s.schedule_id,
            start_time: editStart ? dateToMinutes(editStart) : 0,
            end_time: editEnd ? dateToMinutes(editEnd) : 23 * 60 + 59,
            day_of_week: editDays,
            schedule_name: editName,
          };
          await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/updateSchedule`, payload);
          // 模仿 Android 的节奏，逐条延迟 1 秒
          if (i < spaEditingGroup.length - 1) {
            await new Promise(res => setTimeout(res, 1000));
          }
        }
      } catch (e) {
        console.error('Failed to update SPA group schedules:', e);
      } finally {
        setSpaEditingGroup(null);
        setIsEditOpen(false);
        await fetchScheduleInfo();
        dispatch(setLoading(false));
      }
      return;
    }

    const payload: any = {
      aux_slot: editingSchedule.aux_slot,
      schedule_num: editingSchedule.schedule_id,
      start_time: editStart ? dateToMinutes(editStart) : 0,
      end_time: editEnd ? dateToMinutes(editEnd) : 23 * 60 + 59,
      day_of_week: editDays,
      schedule_name: editName,
    };

    // 主泵时带上泵速（pool_volume 后端可选，前端暂不编辑）
    if (editingSchedule.aux_type_code === AuxTypeCode.SPALSHME_PUMP) {
      const threeSpeed = isThreeSpeedPump(pumpInfo?.pump_brand, pumpInfo?.pump_model);
      const raw = schedulePumpSpeed ?? 0;
      payload.pump_speed = threeSpeed
        ? snapThreeSpeedSpeed(raw > 0 ? raw : THREE_SPEED_LOW)
        : (raw > 0 ? raw : 50);
    }

    dispatch(setLoading(true));
    try {
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/updateSchedule`, payload);
      if (editingSchedule.aux_type_code === AuxTypeCode.SPALSHME_PUMP) {
        const linkedSchedules = getLinkedDeviceSchedules(editingSchedule.schedule_id);
        for (const linkedSchedule of linkedSchedules) {
          const payload: any = {
            aux_slot: linkedSchedule.aux_slot,
            schedule_num: linkedSchedule.schedule_id,
            start_time: editStart ? dateToMinutes(editStart) : 0,
            end_time: editEnd ? dateToMinutes(editEnd) : 23 * 60 + 59,
            day_of_week: editDays,
            schedule_name: linkedSchedule.schedule_name,
          };
          await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/updateSchedule`, payload);
        }
      }
    } catch (e) {
      console.error('Failed to update schedule:', e);
    } finally {
      await new Promise(res => setTimeout(res, 3000));
      setIsEditOpen(false);
      await fetchScheduleInfo();
      dispatch(setLoading(false));
    }
  };

  return (
    <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 md:px-8 py-2 pb-0">
      <div
        className="flex flex-col gap-3 h-full w-full"
        style={{ marginBottom: "-1px" }}
      >
        <SectionHeader
          title="My Schedules"
          subtitle="Overview of Main Pump, SPA and other devices"
          right={
            <div className="flex items-center gap-2 mr-2">
              <div style={{ minWidth: "220px" }}>
                <DeviceSelector
                  onDeviceChange={onDeviceChange}
                  selectedDeviceId={selectedDeviceId}
                />
              </div>
            </div>
          }
        />

        <div className="h-0.5 bg-gray-200 dark:bg-gray-700 my-2"></div>

        {/* 设备本地时间 */}
        <div className="flex items-center justify-between text-sm ml-1">
          <span
            className={classNames({
              "text-gray-600": mode === "light",
              "text-gray-300": mode === "dark",
            })}
          >
            Device Local Time: {timeText || "Loading..."}
          </span>
          <button
            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
          onClick={() => {
            // Open Add Schedule modal
            setIsAddOpen(true);
            setAddStep('select');
            setSelectMainPump(false);
            setSelectedLinkedSlots([]);
            setSelectedAddDevice(null);
            // Reset defaults for configuration step
            setEditName('');
            setEditStart(null);
            setEditEnd(null);
            setEditDays([false, false, false, false, false, false, false]);
          }}
          >
            <FiPlus className="h-4 w-4" />
            <span className="text-sm font-medium">Add Schedule</span>
          </button>
        </div>

        {/* 主泵排程 */}
        <Card className="mt-3">
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-semibold">Main Pump</h5>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {mainPumpSchedules.map((schedule) => (
                <SchedulePanelOverview
                  key={`${schedule.schedule_id}-${schedule.aux_type_code}`}
                  schedule={{
                    id: schedule.schedule_id.toString(),
                    name: schedule.schedule_name,
                    time: `${formatMinutesToTime(
                      schedule.start_time
                    )} - ${formatMinutesToTime(schedule.end_time)}`,
                    days: getWeekdayArray(schedule.week_days),
                    enabled: schedule.schedule_state,
                  }}
                  linkedDevices={getLinkedDeviceNames(schedule)}
                  onEnabledChange={(enabled) =>
                    handleToggleEnabled(schedule.schedule_id, enabled)
                  }
                  onEdit={() => handleEditSchedule(schedule.schedule_id)}
                  onDelete={() => promptDelete('main_pump', schedule.schedule_id)}
                />
              ))}
              {mainPumpSchedules.length === 0 && (
                <div className="text-sm text-gray-500">
                  No schedules configured for main pump.
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* SPA 设备排程：按设备分组展示，设备名 + 横向排程列表 */}
        <Card className="mt-3">
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-semibold">SPA</h5>
            </div>

            {spaDevices.length === 0 && spaList.length === 0 ? (
              <div className="text-base text-gray-500">No SPA devices found.</div>
            ) : (
              <div className="flex flex-col gap-3">
                {/* 展示 SPA 模式（合并吸水/回水逻辑） */}
                <div className="flex flex-col">
                  <div className="text-base font-medium mb-2">SPA Mode</div>
                  <div className="flex flex-row flex-wrap gap-3 mb-3">
                    {spaModeGroups.length > 0 ? (
                      spaModeGroups.map((group, idx) => {
                        const primary = group[1] || group[0] || group[2] || group[3];
                        if (!primary) return null;
                        return (
                          <SchedulePanelOverview
                            key={`spa-mode-group-${idx}-${primary.schedule_id}`}
                            schedule={{
                              id: primary.schedule_id.toString(),
                              name: `SPA Mode #${idx + 1}`,
                              time: `${formatMinutesToTime(primary.start_time)} - ${formatMinutesToTime(primary.end_time)}`,
                              days: getWeekdayArray(primary.week_days),
                              enabled: primary.schedule_state,
                            }}
                            onEnabledChange={(enabled) => handleSpaToggleEnabled(primary.schedule_id, enabled)}
                            onEdit={() => handleSpaEditSchedule(primary.schedule_id)}
                            onDelete={() => promptDelete('spa_group', primary.schedule_id)}
                          />
                        );
                      })
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400">No SPA mode schedule configured.</div>
                    )}
                  </div>
                  <div className="border-b border-gray-200 dark:border-gray-700"></div>
                </div>

                {/* 展示 spaList */}
                {spaDevices.map((device) => (
                  <div
                    key={`spa-list-${device.aux.aux_type_code}-${device.aux.aux_slot_num}`}
                    className="flex flex-col"
                  >
                    {/* 设备名称 */}
                    <div className="text-base font-medium mb-2">
                      {device.aux.aux_tag || getAuxTypeDesc(device.aux.aux_type_code)}
                    </div>

                    {/* Schedule 一行排 */}
                    <div className="flex flex-row flex-wrap gap-3 mb-3">
                      {device.schedules.map((s) => (
                        <SchedulePanelOverview
                          key={`${s.schedule_id}-${s.aux_type_code}`}
                          schedule={{
                            id: s.schedule_id.toString(),
                            name: s.schedule_name,
                            time: `${formatMinutesToTime(s.start_time)} - ${formatMinutesToTime(s.end_time)}`,
                            days: getWeekdayArray(s.week_days),
                            enabled: s.schedule_state,
                          }}
                          onEnabledChange={(enabled) => handleToggleEnabled(s.schedule_id, enabled)}
                          onEdit={() => handleEditSchedule(s.schedule_id)}
                          onDelete={() => promptDelete('single', s.schedule_id)}
                        />
                      ))}
                    </div>

                    {/* 分隔线（可选） */}
                    <div className="border-b border-gray-200 dark:border-gray-700"></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* 其他设备排程 */}
        <Card className="mt-3">
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-semibold">Other Devices</h5>
            </div>

            {otherDevices.length === 0 ? (
              <div className="text-base text-gray-500 dark:text-gray-400">
                No other devices found.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {otherDevices.map((device) => (
                  <div
                    key={`${device.aux.aux_type_code}-${device.aux.aux_slot_num}`}
                    className="flex flex-col"
                  >
                    {/* 设备名称 */}
                    <div className="text-base font-medium mb-2">
                      {device.aux.aux_tag ||
                        `Device ${device.aux.aux_slot_num}`}
                    </div>

                    {/* schedule 一行排 */}
                    <div className="flex flex-row flex-wrap gap-3 mb-3">
                      {device.schedules.map((s) => (
                        <SchedulePanelOverview
                          key={`${s.schedule_id}-${s.aux_type_code}`}
                          schedule={{
                            id: s.schedule_id.toString(),
                            name: s.schedule_name,
                            time: `${formatMinutesToTime(
                              s.start_time
                            )} - ${formatMinutesToTime(s.end_time)}`,
                            days: getWeekdayArray(s.week_days),
                            enabled: s.schedule_state,
                          }}
                          onEnabledChange={(enabled) =>
                            handleToggleEnabled(s.schedule_id, enabled)
                          }
                          onEdit={() => handleEditSchedule(s.schedule_id)}
                          onDelete={() => promptDelete('single', s.schedule_id)}
                        />
                      ))}
                    </div>

                    {/* 分隔线（可选） */}
                    <div className="border-b border-gray-200"></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* 编辑 Schedule 侧边栏 */}
      <Sidebar
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Schedule"
      >
        {editingSchedule && (
          <div className="flex flex-col gap-4">
            <ScheduleDeviceSummary
              isPump={editingSchedule.aux_type_code === AuxTypeCode.SPALSHME_PUMP}
              deviceName={getAuxTypeDesc(editingSchedule.aux_type_code)}
              pumpSpeed={
                editingSchedule.aux_type_code === AuxTypeCode.SPALSHME_PUMP
                  ? schedulePumpSpeed
                  : editingSchedule.pump_speed
              }
              pumpBrand={pumpInfo?.pump_brand}
              pumpModel={pumpInfo?.pump_model}
              linkedDeviceNames={getLinkedDeviceNames(editingSchedule)}
              onPumpSpeedChange={(speed) => setSchedulePumpSpeed(speed)}
            />

            <ScheduleTimeRepeatForm
              name={editName}
              onNameChange={setEditName}
              start={editStart}
              onStartChange={(d) => setEditStart(d)}
              end={editEnd}
              onEndChange={(d) => setEditEnd(d)}
              days={editDays}
              onToggleDay={(idx) => setEditDays(prev => prev.map((v,i) => i===idx ? !v : v))}
            />

            {/* Save */}
            <div className="flex justify-end">
              <button
                className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
                onClick={handleSaveSchedule}
              >
                Save
              </button>
            </div>
          </div>
        )}
      </Sidebar>
      {/* Add Schedule Modal */}
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title={addStep === 'select' ? 'Add Schedule' : 'Configure Schedule'}
        size="lg"
        footer={(
          <>
            <button
              className="px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={() => setIsAddOpen(false)}
            >
              Cancel
            </button>
            {addStep === 'select' ? (
              <button
                className="px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                onClick={() => {
                  // 根据选择的设备设置默认名称（tag）和时间范围（00:00 - 23:59）
                  const start = new Date();
                  start.setHours(0, 0, 0, 0);
                  const end = new Date();
                  end.setHours(23, 59, 0, 0);

                  const defaultName = (() => {
                    if (selectedAddDevice?.kind === 'pump') {
                      return pumpAux?.aux_tag || 'Main Pump';
                    }
                    if (selectedAddDevice?.kind === 'spa') {
                      // spa 分组无具体 aux，使用通用名称
                      return 'SPA Mode';
                    }
                    const aux = selectedAddDevice?.aux;
                    return aux?.aux_tag || (aux ? getAuxTypeDesc(aux.aux_type_code) : 'Device');
                  })();

                  setEditName(defaultName);
                  setEditStart(start);
                  setEditEnd(end);
                  setEditDays([true, true, true, true, true, true, true]);
                  setAddStep('configure');
                }}
                disabled={!selectedAddDevice}
              >
                Next
              </button>
            ) : (
              <button
                className="px-3 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700"
                onClick={async () => {
                  // 创建排程：支持主泵及其联动设备，以及普通设备
                  if (!selectedDeviceId) return;

                  const dateToMinutes = (d: Date) => d.getHours() * 60 + d.getMinutes();
                  const startMinutes = editStart ? dateToMinutes(editStart) : 0;
                  const endMinutes = editEnd ? dateToMinutes(editEnd) : 23 * 60 + 59;
                  const days = editDays;

                  const requests: any[] = [];

                  if (selectedAddDevice?.kind === 'pump' && pumpAux?.aux_slot_num != null) {
                    // 主泵：需要带上 pump_speed 与 pool_volume(默认 0)
                    const pumpPayload: any = {
                      aux_slot: pumpAux.aux_slot_num,
                      start_time: startMinutes,
                      end_time: endMinutes,
                      day_of_week: days,
                      schedule_name: editName || (pumpAux?.aux_tag || 'Main Pump'),
                      pool_volume: 0,
                    };
                    {
                      const threeSpeed = isThreeSpeedPump(pumpInfo?.pump_brand, pumpInfo?.pump_model);
                      const raw = schedulePumpSpeed ?? 0;
                      pumpPayload.pump_speed = threeSpeed
                        ? snapThreeSpeedSpeed(raw > 0 ? raw : THREE_SPEED_LOW)
                        : (raw > 0 ? raw : 50);
                    }
                    requests.push(pumpPayload);

                    // 为每个选中的联动设备分别创建排程（不带 pump 字段）
                    for (const slot of selectedLinkedSlots) {
                      const aux = (manual?.pump_linked_devices || []).find((a) => a.aux_slot_num === slot);
                      const name = aux?.aux_tag || (aux ? getAuxTypeDesc(aux.aux_type_code) : `Slot ${slot}`);
                      requests.push({
                        aux_slot: slot,
                        start_time: startMinutes,
                        end_time: endMinutes,
                        day_of_week: days,
                        schedule_name: name,
                      });
                    }
                  } else if (selectedAddDevice?.kind === 'spa') {
                    // SPA 模式：根据可用的 SPA 执行器（Return/Suction/Extra1/Extra2）分别创建排程
                    const spaActuators = [
                      { type: AuxTypeCode.SPA_ACTUATOR_RETURN, name: 'SPA Mode(Return)' },
                      { type: AuxTypeCode.SPA_ACTUATOR_SUCTION, name: 'SPA Mode(Suction)' },
                      { type: AuxTypeCode.SPA_ACTUATOR_EXTRA_1, name: 'SPA Mode(Extra 1)' },
                      { type: AuxTypeCode.SPA_ACTUATOR_EXTRA_2, name: 'SPA Mode(Extra 2)' },
                    ];
                    const spaList = manual?.spa || [];
                    for (const act of spaActuators) {
                      const aux = spaList.find((d) => d.aux_type_code === act.type);
                      if (aux && aux.aux_slot_num != null) {
                        requests.push({
                          aux_slot: aux.aux_slot_num,
                          start_time: startMinutes,
                          end_time: endMinutes,
                          day_of_week: days,
                          schedule_name: act.name,
                        });
                      }
                    }
                    // 若没有找到任何 SPA 执行器，则直接退出
                    if (requests.length === 0) {
                      setIsAddOpen(false);
                      return;
                    }
                  } else if (selectedAddDevice?.kind === 'spa_device' && selectedAddDevice.aux?.aux_slot_num != null) {
                    // 单个 SPA 设备
                    const aux = selectedAddDevice.aux;
                    requests.push({
                      aux_slot: aux.aux_slot_num,
                      start_time: startMinutes,
                      end_time: endMinutes,
                      day_of_week: days,
                      schedule_name: editName || (aux.aux_tag || getAuxTypeDesc(aux.aux_type_code)),
                    });
                  } else if (selectedAddDevice?.kind === 'other_device' && selectedAddDevice.aux?.aux_slot_num != null) {
                    const aux = selectedAddDevice.aux;
                    requests.push({
                      aux_slot: aux.aux_slot_num,
                      start_time: startMinutes,
                      end_time: endMinutes,
                      day_of_week: days,
                      schedule_name: editName || (aux.aux_tag || getAuxTypeDesc(aux.aux_type_code)),
                    });
                  } else {
                    
                    setIsAddOpen(false);
                    return;
                  }

                  dispatch(setLoading(true));
                  try {
                    for (let i = 0; i < requests.length; i++) {
                      const payload = requests[i];
                      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/createSchedule`, payload);
                      if (i < requests.length - 1) {
                        // 模仿顺序提交的节奏
                        await new Promise((res) => setTimeout(res, 1000));
                      }
                    }
                  } catch (e) {
                    console.error('Failed to create schedule(s):', e);
                  } finally {
                    // 等待设备处理完成后刷新
                    await new Promise((res) => setTimeout(res, 3000));
                    setIsAddOpen(false);
                    setAddStep('select');
                    setSelectedLinkedSlots([]);
                    await fetchScheduleInfo();
                    dispatch(setLoading(false));
                  }
                }}
              >
                Save
              </button>
            )}
          </>
        )}
      >
        {addStep === 'select' ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-md border p-4">
              <div className="flex flex-col gap-2">
                {/* Main Pump - radio */}
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="add-device-radio"
                    checked={selectedAddDevice?.kind === 'pump'}
                    onChange={() => {
                      setSelectedAddDevice({ kind: 'pump', aux: pumpAux || undefined });
                      setSelectMainPump(true);
                      {
                        const threeSpeed = isThreeSpeedPump(pumpInfo?.pump_brand, pumpInfo?.pump_model);
                        const raw = pumpAux?.pump_speed ?? 0;
                        setSchedulePumpSpeed(
                          threeSpeed
                            ? snapThreeSpeedSpeed(raw > 0 ? raw : THREE_SPEED_LOW)
                            : (raw > 0 ? raw : 50)
                        );
                      }
                      // 选择主泵时，自动勾选所有挂载的 Linked Devices
                      setSelectedLinkedSlots((manual?.pump_linked_devices || []).map((d) => d.aux_slot_num));
                    }}
                  />
                  <span className="text-sm">{pumpAux?.aux_tag || 'Main Pump'}</span>
                </label>

                {/* Linked Devices under Main Pump - checkboxes */}
                <div className="flex flex-col gap-2 ml-6">
                  {(manual?.pump_linked_devices?.length || 0) === 0 ? (
                    <span className="text-sm text-gray-500"></span>
                  ) : (
                    (manual?.pump_linked_devices || []).map((aux) => {
                      const slot = aux.aux_slot_num;
                      const checked = selectedLinkedSlots.includes(slot);
                      return (
                        <label key={`linked-${slot}`} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                setSelectedLinkedSlots((prev) => Array.from(new Set([...prev, slot])));
                                // Selecting linked device forces main pump selected
                                setSelectedAddDevice({ kind: 'pump', aux: pumpAux || undefined });
                                setSelectMainPump(true);
                                {
                                  const threeSpeed = isThreeSpeedPump(pumpInfo?.pump_brand, pumpInfo?.pump_model);
                                  const raw = pumpAux?.pump_speed ?? 0;
                                  setSchedulePumpSpeed(
                                    threeSpeed
                                      ? snapThreeSpeedSpeed(raw > 0 ? raw : THREE_SPEED_LOW)
                                      : (raw > 0 ? raw : 50)
                                  );
                                }
                              } else {
                                setSelectedLinkedSlots((prev) => prev.filter((s) => s !== slot));
                              }
                            }}
                          />
                          <span className="text-sm">
                            {aux.aux_tag || getAuxTypeDesc(aux.aux_type_code)}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>

                {/* SPA - single radio */}
                {(manual?.spa?.length || 0) > 0 && (
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="add-device-radio"
                      checked={selectedAddDevice?.kind === 'spa'}
                      onChange={() => {
                        setSelectedAddDevice({ kind: 'spa' });
                        setSelectMainPump(false);
                        setSelectedLinkedSlots([]);
                      }}
                    />
                    <span className="text-sm">SPA</span>
                  </label>
                )}

                {/* SPA devices - radios */}
                {(manual?.spa_devices || []).map((aux) => (
                  <label
                    key={`spa-dev-${aux.aux_type_code}-${aux.aux_slot_num}`}
                    className="flex items-center gap-2"
                  >
                    <input
                      type="radio"
                      name="add-device-radio"
                      checked={
                        selectedAddDevice?.kind === 'spa_device' &&
                        selectedAddDevice?.aux?.aux_slot_num === aux.aux_slot_num &&
                        selectedAddDevice?.aux?.aux_type_code === aux.aux_type_code
                      }
                      onChange={() => {
                        setSelectedAddDevice({ kind: 'spa_device', aux });
                        setSelectMainPump(false);
                        setSelectedLinkedSlots([]);
                      }}
                    />
                    <span className="text-sm">
                      {aux.aux_tag || getAuxTypeDesc(aux.aux_type_code)}
                    </span>
                  </label>
                ))}

                {/* Other devices - radios */}
                {(manual?.other_devices || []).map((aux) => (
                  <label
                    key={`other-dev-${aux.aux_type_code}-${aux.aux_slot_num}`}
                    className="flex items-center gap-2"
                  >
                    <input
                      type="radio"
                      name="add-device-radio"
                      checked={
                        selectedAddDevice?.kind === 'other_device' &&
                        selectedAddDevice?.aux?.aux_slot_num === aux.aux_slot_num &&
                        selectedAddDevice?.aux?.aux_type_code === aux.aux_type_code
                      }
                      onChange={() => {
                        setSelectedAddDevice({ kind: 'other_device', aux });
                        setSelectMainPump(false);
                        setSelectedLinkedSlots([]);
                      }}
                    />
                    <span className="text-sm">
                      {aux.aux_tag || getAuxTypeDesc(aux.aux_type_code)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <ScheduleDeviceSummary
              isPump={selectedAddDevice?.kind === 'pump'}
              deviceName={
                selectedAddDevice?.kind === 'pump'
                  ? (pumpAux?.aux_tag || 'Main Pump')
                  : selectedAddDevice?.kind === 'spa'
                    ? 'SPA'
                    : (selectedAddDevice?.aux?.aux_tag || (selectedAddDevice?.aux ? getAuxTypeDesc(selectedAddDevice.aux.aux_type_code) : 'Device'))
              }
              pumpSpeed={selectedAddDevice?.kind === 'pump' ? schedulePumpSpeed : undefined}
              pumpBrand={pumpInfo?.pump_brand}
              pumpModel={pumpInfo?.pump_model}
              linkedDeviceNames={
                selectedAddDevice?.kind === 'pump'
                  ? selectedLinkedSlots.map((slot) => {
                      const dev = (manual?.pump_linked_devices || []).find((d) => d.aux_slot_num === slot);
                      return (
                        dev?.aux_tag ||
                        (dev ? getAuxTypeDesc(dev.aux_type_code) : 'Linked Device')
                      );
                    })
                  : []
              }
              onPumpSpeedChange={(speed) => setSchedulePumpSpeed(speed)}
            />
            <ScheduleTimeRepeatForm
              name={editName}
              onNameChange={setEditName}
              start={editStart}
              onStartChange={(d) => setEditStart(d)}
              end={editEnd}
              onEndChange={(d) => setEditEnd(d)}
              days={editDays}
              onToggleDay={(idx) => setEditDays((prev) => prev.map((v, i) => (i === idx ? !v : v)))}
            />
          </div>
        )}
      </Modal>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Confirm Delete"
        onCancel={() => { setConfirmDeleteOpen(false); setDeleteTarget(null); }}
        onOk={confirmDelete}
        cancelText="Cancel"
        okText="Delete"
      >
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {deleteTarget?.kind === 'main_pump'
            ? 'Deleting a main pump schedule will also remove linked device schedules.'
            : 'Are you sure you want to delete this schedule?'}
        </div>
      </ConfirmDialog>
    </div>
  );
};

export default ScheduleOverviewPage;
